// Registers the service worker, and reports when a new version is waiting.
//
// Adapted from cra-template-pwa. The localhost branch that template carries
// (checking for, and unregistering, a stale worker left over on a dev port)
// is dropped: this app never registers a worker in development, so there is
// nothing to clean up. Production only, so the dev server, jsdom and the e2e
// dev project behave exactly as they did before.

/**
 * @param {{ onUpdate?: (registration: ServiceWorkerRegistration) => void,
 *           onSuccess?: (registration: ServiceWorkerRegistration) => void }} config
 * @param {{ env?: string, nav?: Navigator, win?: Window }} deps injected for tests.
 * @returns {boolean} whether registration was attempted.
 */
export function register(config = {}, deps = {}) {
  const env = deps.env ?? process.env.NODE_ENV;
  const nav = deps.nav ?? (typeof navigator !== 'undefined' ? navigator : undefined);
  const win = deps.win ?? (typeof window !== 'undefined' ? window : undefined);

  if (env !== 'production' || !nav || !('serviceWorker' in nav) || !win) return false;

  const swUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;
  const start = () => {
    nav.serviceWorker
      .register(swUrl)
      .then((registration) => {
        // A worker already waiting when the page loads (downloaded during an
        // earlier visit, never activated) is an update too. Without this, an
        // app that is opened and closed without ever seeing the install event
        // would never offer it.
        if (registration.waiting && nav.serviceWorker.controller) {
          config.onUpdate?.(registration);
        }

        registration.onupdatefound = () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.onstatechange = () => {
            if (installing.state !== 'installed') return;
            if (nav.serviceWorker.controller) {
              // An old worker still controls the page: this one waits.
              config.onUpdate?.(registration);
            } else {
              // First install. Everything is precached for offline use.
              config.onSuccess?.(registration);
            }
          };
        };
      })
      .catch((error) => {
        // Never fatal: the app works online without a worker.
        console.warn('Service worker registration failed:', error);
      });
  };

  if (win.document?.readyState === 'complete') start();
  else win.addEventListener('load', start);
  return true;
}
